import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { InfoCard } from '~/eds/src/components/Card/subcomponents/InfoCard';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { UserAction } from '~/eds/src/types/UserAction';

import { ClauseTypeSelect } from './ClauseTypeSelect';
import { Top3ClauseTypes } from './Top3ClauseTypes';
import { ClauseType, Stage } from './types';

interface Props {
  onContinue: () => void;
  onSkip: () => void;
}

// TODO: REMOVE MOCK DATA
const mockClauseTypes: ClauseType[] = [
  {
    name: 'Acceptance',
    id: 'acceptance',
    count: 363,
  },
  {
    name: 'Assignment',
    id: 'assignment',
    count: 421,
  },
  {
    name: 'Confidentiality',
    id: 'confidentiality',
    count: 1045,
  },
  {
    name: 'Force Majeure',
    id: 'force-majeure',
    count: 29,
  },
  {
    name: 'Governing Law',
    id: 'governing-law',
    count: 882,
  },
  {
    name: 'Indemnification',
    id: 'indemnification',
    count: 96,
  },
];

const mockQueryResponse = {
  data: mockClauseTypes,
  isFetching: false,
  isSuccess: true,
  error: null,
};

export const WelcomePage = ({ onContinue, onSkip }: Props) => {
  const intl = useIntl();
  const [stage, setStage] = useState<Stage>('welcome');

  const { data: clauseTypes = [], isFetching } = mockQueryResponse; // api.endpoints.getClauseTypes.useQuery({});

  const initiateAiSuggest = (clauseType: ClauseType) => {
    console.log('TODO: initiateAiSuggest for', clauseType.name);
    onContinue();
  };

  let title = '';
  let description = '';
  let navActions: UserAction[] = [];
  let content;

  switch (stage) {
    case 'welcome':
      title = intl.formatMessage({
        id: 'WelcomePage.welcomeTitle',
        defaultMessage: 'Welcome to Clause Library',
        description: 'Welcome page title',
      });
      description = intl.formatMessage({
        id: 'WelcomePage.welcomeDescription',
        defaultMessage:
          'Let Evisort AI suggest clauses for you or skip and enter the clause library to add clauses manually.',
        description: 'Welcome page description',
      });
      content = (
        <Layout direction="column" spacing={8} align="center">
          <Top3ClauseTypes clauseTypes={clauseTypes} isLoading={isFetching} />
          <Layout preset="cards">
            <InfoCard
              action={{
                text: intl.formatMessage({
                  id: 'WelcomePage.getStartedButton',
                  defaultMessage: 'Get Started',
                  description: 'Get started button text',
                }),
                level: 'secondary',
                onClick: () => setStage('ai-suggest-start'),
              }}
              description={intl.formatMessage({
                id: 'WelcomePage.aiSuggestDescription',
                defaultMessage:
                  'Evisort AI makes suggestions based on your past agreements.',
                description: 'AI suggestion feature description',
              })}
              icon="ai"
              title={intl.formatMessage({
                id: 'WelcomePage.aiSuggestTitle',
                defaultMessage: 'Let Evisort AI Suggest',
                description: 'AI suggestion card title',
              })}
            />
          </Layout>
          <Button
            variant="action"
            text={intl.formatMessage({
              id: 'WelcomePage.skipButton',
              defaultMessage: 'Skip and enter clause library',
              description: 'Skip button text',
            })}
            onClick={onSkip}
          />
        </Layout>
      );
      break;
    case 'ai-suggest-start':
      title = intl.formatMessage({
        id: 'WelcomePage.aiSuggestStartTitle',
        defaultMessage: 'Let Evisort AI Suggest',
        description: 'AI suggest start page title',
      });
      description = intl.formatMessage({
        id: 'WelcomePage.aiSuggestStartDescription',
        defaultMessage:
          'Evisort AI will suggest clauses after looking at your executed agreements from the past 2 years. Select a clause type to generate clauses for.',
        description: 'AI suggest start page description',
      });
      navActions = [
        {
          text: intl.formatMessage({
            id: 'WelcomePage.clauseLibraryNav',
            defaultMessage: 'Clause Library',
            description: 'Clause library navigation text',
          }),
          onClick: () => setStage('welcome'),
        },
        {
          text: title,
          disabled: true,
        },
      ];
      content = (
        <ClauseTypeSelect
          clauseTypes={clauseTypes}
          isLoading={isFetching}
          onSubmit={initiateAiSuggest}
        />
      );
      break;
  }

  return (
    <PageLayout
      welcome={{ content: content }}
      title={title}
      description={description}
      nav={{ actions: navActions }}
    />
  );
};
